/**
 * ThreadDetail
 * ------------
 * Full thread view -- header + body + sorted replies + <ReplyForm />.
 *
 * Reply sorting is handled by the backend (product-linked replies by price
 * ascending with dealer tiebreak, then plain replies chronologically). We
 * simply render in the order we receive.
 */
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Eye, MessageCircle } from "lucide-react";
import { api } from "../../lib/api";
import type { ThreadDetailResponse } from "../../lib/types";
import { Badge } from "../../components/ui/Badge";
import { Card, CardBody } from "../../components/ui/Card";
import { ReplyItem } from "../../components/forum/ReplyItem";
import { ReplyForm } from "../../components/forum/ReplyForm";

export default function ThreadDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = React.useState<ThreadDetailResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);

  const load = React.useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const r = await api.get<ThreadDetailResponse>(`/threads/${id}`);
      setData(r.data);
    } catch (err: any) {
      if (err?.response?.status === 404) setNotFound(true);
    } finally {
      setLoading(false);
    }
  }, [id]);

  React.useEffect(() => {
    load();
  }, [load]);

  if (notFound) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="text-3xl font-black mb-2">Thread not found</h1>
        <p className="text-ink-muted">It may have been removed or the link is wrong.</p>
        <button
          onClick={() => navigate("/forums")}
          className="mt-6 text-brand font-semibold hover:text-brand-hover"
        >
          ← Back to the forums
        </button>
      </div>
    );
  }

  if (loading || !data) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-16">
        <div className="h-10 w-10 rounded-full border-4 border-surface-border border-t-brand animate-spin mx-auto" />
      </div>
    );
  }

  const { thread, replies, counts } = data;

  // Compute rank only over product-linked replies (backend returns those first).
  let productRankCounter = 0;

  return (
    <div className="mx-auto max-w-3xl px-4 md:px-6 py-8 md:py-12">
      {/* Thread header */}
      <header className="mb-6" data-testid="thread-header">
        <div className="flex items-center flex-wrap gap-2 mb-3">
          {thread.is_pinned && <Badge variant="brand" size="sm">Pinned</Badge>}
          {thread.is_locked && <Badge variant="muted" size="sm">Locked</Badge>}
          {thread.tags.map((t) => (
            <Badge key={t} variant="outline" size="sm">
              {t}
            </Badge>
          ))}
        </div>
        <h1 className="text-3xl md:text-4xl font-black tracking-tight mb-3">
          {thread.title}
        </h1>
        <div className="flex items-center gap-4 text-sm text-ink-muted">
          <span className="inline-flex items-center gap-1">
            <MessageCircle className="h-4 w-4" aria-hidden /> {counts.total} replies
          </span>
          <span className="inline-flex items-center gap-1">
            <Eye className="h-4 w-4" aria-hidden /> {thread.views} views
          </span>
        </div>
      </header>

      {/* Thread body as a first "post" */}
      <Card elevated className="mb-6">
        <CardBody>
          <p className="text-ink whitespace-pre-line leading-relaxed">{thread.body}</p>
        </CardBody>
      </Card>

      {/* Callout: "products sorted by price" info */}
      {counts.with_product > 0 && (
        <div
          data-testid="thread-price-sort-callout"
          className="mb-5 rounded-xl border-2 border-ink bg-surface-raised px-4 py-3 text-sm flex items-center gap-3"
        >
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-brand text-white font-black text-xs">
            $
          </span>
          <span>
            <strong className="text-ink">{counts.with_product}</strong> parts offered below,
            sorted by lowest price first. Dealer listings break ties.
          </span>
        </div>
      )}

      {/* Replies list */}
      <section
        data-testid="thread-replies"
        className="flex flex-col gap-4 mb-8"
      >
        {replies.map(({ reply, product }) => {
          const rank = product ? ++productRankCounter : undefined;
          return (
            <ReplyItem
              key={reply.id}
              reply={reply}
              product={product}
              rank={rank}
            />
          );
        })}
        {replies.length === 0 && (
          <Card>
            <CardBody>
              <p className="text-ink-muted">No replies yet. Be the first to chime in.</p>
            </CardBody>
          </Card>
        )}
      </section>

      {/* Reply form */}
      {!thread.is_locked ? (
        <ReplyForm threadId={thread.id} onPosted={load} />
      ) : (
        <div
          data-testid="thread-locked-notice"
          className="rounded-2xl border border-surface-border bg-surface-raised p-6 text-center text-ink-muted"
        >
          This thread is locked. New replies are disabled.
        </div>
      )}
    </div>
  );
}
